import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { api, fmt, errMsg, eventTypes, fmtRange } from "@/lib/api";
import Calendar from "@/components/Calendar";

export default function NewBooking() {
  const navigate = useNavigate();
  const [venueId, setVenueId] = useState(null);
  const [pkgId, setPkgId] = useState(null);
  const [addonIds, setAddonIds] = useState([]);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState({ start: "18:00", end: "22:00" });
  const [form, setForm] = useState({ name: "", phone: "", event_type: "عرس", guests: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const { data: venues } = useQuery({ queryKey: ["admin-venues"], queryFn: async () => (await api.get("/admin/venues")).data });
  useEffect(() => { if (venues?.length && !venueId) setVenueId(venues[0].id); }, [venues, venueId]);
  useEffect(() => {
    const v = venues?.find((x) => x.id === venueId);
    const slots = (v?.time_slots || []).filter((s) => s.active !== false);
    if (slots.length) setTime({ start: slots[0].start, end: slots[0].end });
  }, [venueId, venues]);

  const { data: packages } = useQuery({ queryKey: ["admin-packages", venueId], enabled: !!venueId, queryFn: async () => (await api.get("/admin/packages", { params: { venue_id: venueId } })).data });
  const { data: addons } = useQuery({ queryKey: ["admin-addons", venueId], enabled: !!venueId, queryFn: async () => (await api.get("/admin/addons", { params: { venue_id: venueId } })).data });

  const pkg = packages?.find((p) => p.id === pkgId);
  const total = useMemo(() => (pkg?.price || 0) + (addons || []).filter((a) => addonIds.includes(a.id)).reduce((s, a) => s + a.price, 0), [pkg, addons, addonIds]);

  const submit = async () => {
    if (!pkgId || !date || !form.name.trim() || !form.phone.trim() || !form.guests) return toast.error("أكمل الحقول المطلوبة");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bookings", {
        venue_id: venueId, package_id: pkgId, addon_ids: addonIds, date,
        start_time: time.start, end_time: time.end,
        name: form.name, phone: form.phone, event_type: form.event_type,
        guests: Number(form.guests), notes: form.notes, terms_agreed: true,
      });
      toast.success("تم إنشاء الحجز");
      navigate(`/admin/booking/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="space-y-5 pb-6" data-testid="new-booking">
      <h1 className="font-display text-2xl">حجز جديد</h1>

      <Block label="المكان">
        <div className="flex gap-2 flex-wrap">
          {(venues || []).map((v) => (
            <button key={v.id} onClick={() => { setVenueId(v.id); setPkgId(null); setAddonIds([]); }}
              className={`px-4 py-2 rounded-full text-sm ${venueId === v.id ? "btn-gold" : "card-lux text-muted2"}`}>{v.name}</button>
          ))}
        </div>
      </Block>

      <Block label="الباقة">
        <div className="space-y-2">
          {(packages || []).map((p) => (
            <button key={p.id} data-testid={`np-${p.id}`} onClick={() => setPkgId(p.id)}
              className={`card-lux p-3 w-full flex justify-between items-center text-start ${pkgId === p.id ? "ring-2 ring-gold" : ""}`}>
              <span className="text-offwhite">{p.name}</span><span className="gold-text text-sm">{fmt(p.price)}</span>
            </button>
          ))}
        </div>
      </Block>

      <Block label="الإضافات">
        <div className="space-y-2">
          {(addons || []).map((a) => {
            const on = addonIds.includes(a.id);
            return (
              <button key={a.id} onClick={() => setAddonIds((p) => on ? p.filter((x) => x !== a.id) : [...p, a.id])}
                className={`card-lux p-3 w-full flex items-center gap-3 text-start ${on ? "ring-1 ring-gold" : ""}`}>
                <span className={`w-5 h-5 rounded border flex items-center justify-center ${on ? "bg-gold border-gold" : "border-muted2"}`}>{on && <Check size={14} className="text-navy" />}</span>
                <span className="flex-1 text-offwhite text-sm">{a.name}</span><span className="gold-text text-xs">+ {fmt(a.price)}</span>
              </button>
            );
          })}
        </div>
      </Block>

      <Block label="التاريخ">
        {venueId && <Calendar venueId={venueId} selectedDate={date} onSelect={setDate} />}
        <div className="mt-4">
          <label className="text-sm text-muted2 mb-2 block">وقت الحفلة</label>
          {(() => {
            const v = venues?.find((x) => x.id === venueId);
            const slots = (v?.time_slots || []).filter((s) => s.active !== false);
            if (!slots.length) return <p className="text-xs text-muted2">وقت الحفلة: {fmtRange(time.start, time.end)}</p>;
            return (
              <div className="grid gap-2">
                {slots.map((s, i) => {
                  const on = time.start === s.start && time.end === s.end;
                  return (
                    <button key={i} data-testid={`nslot-${i}`} onClick={() => setTime({ start: s.start, end: s.end })}
                      className={`py-2.5 rounded-xl text-sm ${on ? "btn-gold" : "card-lux text-offwhite"}`}>{fmtRange(s.start, s.end)}</button>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </Block>

      <Block label="بيانات الزبون">
        <div className="space-y-3">
          <Inp label="الاسم الكامل" val={form.name} on={(v) => setForm({ ...form, name: v })} />
          <Inp label="رقم الهاتف" val={form.phone} on={(v) => setForm({ ...form, phone: v })} type="tel" />
          <div>
            <label className="text-xs text-muted2 mb-1 block">نوع المناسبة</label>
            <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className="w-full card-lux p-2.5 bg-navy2 text-offwhite outline-none">
              {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <Inp label="عدد الضيوف" val={form.guests} on={(v) => setForm({ ...form, guests: v.replace(/[^0-9]/g, "") })} type="tel" />
          <Inp label="ملاحظات (اختياري)" val={form.notes} on={(v) => setForm({ ...form, notes: v })} />
        </div>
      </Block>

      <div className="card-lux p-4 flex items-center justify-between">
        <span className="text-muted2 text-sm">المجموع التقديري</span>
        <span className="gold-text font-bold">{fmt(total)}</span>
      </div>
      <button onClick={submit} disabled={busy} data-testid="create-booking-btn" className="btn-gold w-full py-3.5 rounded-full flex items-center justify-center gap-2">
        {busy && <Loader2 className="animate-spin" size={18} />} إنشاء الحجز
      </button>
    </div>
  );
}

const Block = ({ label, children }) => (<div><h2 className="text-sm text-muted2 mb-2">{label}</h2>{children}</div>);
const Inp = ({ label, val, on, type = "text" }) => (
  <div>
    <label className="text-xs text-muted2 mb-1 block">{label}</label>
    <input type={type} value={val} onChange={(e) => on(e.target.value)} className="w-full card-lux p-2.5 bg-navy2 text-offwhite outline-none focus:ring-1 focus:ring-gold" />
  </div>
);
